import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Dialog, Spinner } from "@blueprintjs/core";

import { withToaster } from "@utils";

import { getTokens } from "@pages/settings/api";
import { ManageTokenDialog } from "@pages/settings/components/ManageTokenDialog";
import { Token } from "@pages/settings/types";

const Container = styled.div`
  overflow: "wrap";
`;

const DescriptionContainer = styled.div`
  margin-top: var(--global-spacing-l);
  max-width: 500px;
`;

type Props = {
  orgId: string;
};

export const TokenSettingsPanel: React.FC<Props> = ({ orgId }) => {
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState<boolean>(false);
  const [selectedToken, setSelectedToken] = useState<Token>();

  useEffect(() => {
    let isMounted = true;
    if (hasChanges) {
      setIsLoading(true);
    }
    withToaster(
      () =>
        getTokens(orgId).then((response) => {
          if (!isMounted) return;
          setTokens(response.tokens.sort((a, b) => b.id - a.id));
        }),
      (err) => `Failed to load tokens: ${err.body?.error}`
    )().finally(() => {
      if (!isMounted) return;
      setHasChanges(false);
      setIsLoading(false);
    });

    // Abort any callbacks/promises on component unmounting
    return () => {
      isMounted = false;
    };
  }, [hasChanges, orgId]);

  return (
    <Container>
      <Dialog
        isOpen={isTokenDialogOpen}
        onClose={() => {
          setIsTokenDialogOpen(false);
          if (!selectedToken) setHasChanges(true);
          setSelectedToken(undefined);
        }}
      >
        <ManageTokenDialog
          onFinish={() => {
            setIsTokenDialogOpen(false);
            setSelectedToken(undefined);
            setHasChanges(true);
          }}
          orgId={orgId}
          existingToken={selectedToken}
        />
      </Dialog>
      <DescriptionContainer>
        <p>
          Semgrep uses API tokens to verify your identity and prevent
          unauthorized users from scanning your code.
        </p>
        <p>
          For GitHub Actions users, you can save these tokens in your
          <a
            href="https://docs.github.com/en/actions/configuring-and-managing-workflows/creating-and-storing-encrypted-secrets"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            GitHub Secrets
          </a>
          . If you do not use GitHub Actions, you will need to securely
          propagate this secret to your CI workflow file.
        </p>
      </DescriptionContainer>
      {tokens.length > 0 && (
        <div style={{ marginTop: "12px" }}>
          If you already have a token configured, you do not need a new one.
        </div>
      )}
      <Button
        style={{ marginBottom: "10px", marginTop: "10px" }}
        intent={tokens.length === 0 ? "primary" : "success"}
        onClick={async () => {
          setIsTokenDialogOpen(true);
        }}
        data-testid="create-token-button"
      >
        Create new token
      </Button>
      {isLoading ? (
        <Spinner intent="primary" />
      ) : (
        tokens &&
        tokens.map((token, index) => {
          return (
            <div
              key={index}
              style={{ display: "flex" }}
              data-testid={`${token.name}-row`}
            >
              <p
                style={{
                  width: "520px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  marginTop: "6px",
                }}
              >
                {token.name}
              </p>
              <Button
                style={{
                  marginLeft: "10px",
                  marginBottom: "4px",
                  width: "50px",
                }}
                onClick={() => {
                  setSelectedToken(token);
                  setIsTokenDialogOpen(true);
                }}
                intent="warning"
                minimal
                data-testid={`edit-button`}
              >
                edit
              </Button>
            </div>
          );
        })
      )}
    </Container>
  );
};
